import React from "react";

import { ButtonStandard, ModalBase, Text } from "@performyard/py-components";

export interface ArchiveModalProps {
	isOpen: boolean;
	roleName: string;
	employeeCount: number;
	// Unsaved bulk level edits on the role's Competencies tab — archiving discards them, so the
	// confirm calls that out and asks for an explicit discard.
	pendingEditCount?: number;
	onConfirm: () => void;
	onClose: () => void;
	onViewEmployees: () => void;
}

const ArchiveModal = ({
	isOpen,
	roleName,
	employeeCount,
	pendingEditCount = 0,
	onConfirm,
	onClose,
	onViewEmployees,
}: ArchiveModalProps) => {
	const isBlocked = employeeCount > 0;

	if (isBlocked) {
		const employeeWord = employeeCount === 1 ? "employee" : "employees";
		return (
			<ModalBase isOpen={isOpen} onClose={onClose}>
				<div className="box-border w-[432px] px-6 pb-6">
					<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
						Reassign employees before archiving
					</Text>
					<Text size="paragraph-1" dangerouslySetClassName="mt-4 text-center">
						&quot;{roleName}&quot; has {employeeCount} assigned {employeeWord}. Reassign them to another
						role before archiving.
					</Text>
					<div className="mt-6 flex justify-center gap-2">
						<div>
							<ButtonStandard size="large" variant="outline" color="neutral" onClick={onClose}>
								Cancel
							</ButtonStandard>
						</div>
						<div>
							<ButtonStandard size="large" color="primary" onClick={onViewEmployees}>
								View Employees
							</ButtonStandard>
						</div>
					</div>
				</div>
			</ModalBase>
		);
	}

	const hasPendingEdits = pendingEditCount > 0;
	return (
		<ModalBase isOpen={isOpen} onClose={onClose}>
			<div className="box-border w-[432px] px-6 pb-6">
				<Text size="sub-heading" weight="semi-bold" dangerouslySetClassName="text-center">
					{hasPendingEdits ? `Archive ${roleName}?` : "Archive Role?"}
				</Text>
				<Text size="paragraph-1" dangerouslySetClassName="mt-4 text-center">
					{hasPendingEdits
						? `You have ${pendingEditCount} unsaved competency ${
								pendingEditCount === 1 ? "change" : "changes"
							}. Archiving will discard them.`
						: `Are you sure you want to archive the role "${roleName}"?`}
				</Text>
				<div className="mt-6 flex justify-center gap-2">
					<div>
						<ButtonStandard size="large" variant="outline" color="neutral" onClick={onClose}>
							Cancel
						</ButtonStandard>
					</div>
					<div>
						<ButtonStandard size="large" color="primary" onClick={onConfirm}>
							{hasPendingEdits ? "Discard changes and archive" : "Yes, Archive Role"}
						</ButtonStandard>
					</div>
				</div>
			</div>
		</ModalBase>
	);
};

export default ArchiveModal;
